import { FRAME_RATE } from "./constants.js";

export const LEGACY_LETTER_TO_ACTIONS = {
  n: [],
  d: ["right"],
  a: ["left"],
  w: ["up"],
  q: ["up", "left"],
  e: ["up", "right"],
  R: ["left", "right"],
  S: ["left", "right"],
  W: ["left", "right", "up"],
};

export const LEGACY_LEGEND = [
  { letter: "n", label: "No input" },
  { letter: "d", label: "Right" },
  { letter: "a", label: "Left" },
  { letter: "w", label: "Up" },
  { letter: "q", label: "Up + Left" },
  { letter: "e", label: "Up + Right" },
  { letter: "R", label: "Left + Right" },
  { letter: "S", label: "Left + Right" },
  { letter: "W", label: "Left + Right + Up" },
];

const BUTTON_ORDER = ["up", "right", "left"];
const BUTTONS_TO_LETTER = new Map(
  Object.entries(LEGACY_LETTER_TO_ACTIONS).map(([letter, actions]) => [serializeActions(actions), letter]),
);

function serializeActions(actions) {
  return JSON.stringify([...actions].sort());
}

function frameToTimestamp(frame) {
  return `${Math.floor(frame / FRAME_RATE)}:${frame % FRAME_RATE}`;
}

function timestampToFrame(timestamp) {
  const [seconds, frame] = String(timestamp).split(":").map(Number);
  return seconds * FRAME_RATE + frame;
}

export function convertLegacyInputToEvents(text) {
  const tokens = [...String(text ?? "").matchAll(/([a-zA-Z])(\d+)/g)];
  if (tokens.length === 0) {
    throw new Error("No valid legacy input tokens found.");
  }
  const events = [];
  let frame = 0;
  let previousActions = new Set();
  for (const [, letter, durationText] of tokens) {
    if (!(letter in LEGACY_LETTER_TO_ACTIONS)) {
      throw new Error(`Unknown legacy input letter: '${letter}'.`);
    }
    const currentActions = new Set(LEGACY_LETTER_TO_ACTIONS[letter]);
    const duration = Number.parseInt(durationText, 10);
    for (const action of BUTTON_ORDER.filter((entry) => previousActions.has(entry) && !currentActions.has(entry))) {
      events.push(`${frameToTimestamp(frame)} rel ${action}`);
    }
    for (const action of BUTTON_ORDER.filter((entry) => currentActions.has(entry) && !previousActions.has(entry))) {
      events.push(`${frameToTimestamp(frame)} press ${action}`);
    }
    frame += duration;
    previousActions = currentActions;
  }
  for (const action of BUTTON_ORDER.filter((entry) => previousActions.has(entry))) {
    events.push(`${frameToTimestamp(frame)} rel ${action}`);
  }
  return events.join("\n");
}

export function convertEventsToLegacyInput(text) {
  const lines = String(text ?? "").trim().split("\n").map((line) => line.trim()).filter(Boolean);
  const rawEvents = [];
  for (const line of lines) {
    const parts = line.split(/\s+/);
    if (parts.length !== 3) {
      throw new Error(`Bad event line: '${line}'.`);
    }
    const [timestamp, action, button] = parts;
    if (!["press", "rel"].includes(action)) {
      throw new Error(`Unknown action: '${action}'.`);
    }
    if (!["up", "right", "left"].includes(button)) {
      throw new Error(`Unknown button: '${button}'.`);
    }
    rawEvents.push([timestampToFrame(timestamp), action, button]);
  }
  rawEvents.sort((left, right) => left[0] - right[0]);
  const heldActions = new Set();
  let currentFrame = 0;
  let result = "";
  let index = 0;
  while (index < rawEvents.length) {
    const frame = rawEvents[index][0];
    const duration = frame - currentFrame;
    if (duration > 0) {
      const letter = BUTTONS_TO_LETTER.get(serializeActions(heldActions));
      if (!letter) {
        throw new Error(`No legacy input letter matches {${[...heldActions].join(", ")}}.`);
      }
      result += `${letter}${duration}`;
    }
    currentFrame = frame;
    while (index < rawEvents.length && rawEvents[index][0] === frame) {
      const [, action, button] = rawEvents[index];
      if (action === "press") {
        heldActions.add(button);
      } else {
        heldActions.delete(button);
      }
      index += 1;
    }
  }
  return result;
}
